import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { invalidateLoanActivity } from '@/hooks/lib/invalidate-loan-activity';
import { api } from '@/lib/api';

export function useApprovalWorkflows(loanId: string) {
  return useQuery({
    queryKey: ['approval-workflows', loanId],
    queryFn: async () => {
      const { data } = await api.get(`/loans/${loanId}/approvals/workflows`);
      return data;
    },
    enabled: !!loanId,
  });
}

export function usePendingApprovals() {
  return useQuery({
    queryKey: ['pending-approvals'],
    queryFn: async () => {
      const { data } = await api.get('/approvals/pending');
      return data;
    },
  });
}

export function useApprovalStats() {
  return useQuery({
    queryKey: ['approval-stats'],
    queryFn: async () => {
      const { data } = await api.get('/approvals/stats');
      return data;
    },
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}

// Custom action endpoint /approval-workflow — keep manual
export function useCreateApprovalWorkflow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ loanId, notes }: { loanId: string; notes?: string }) => {
      const { data } = await api.post(`/loans/${loanId}/approvals/workflow`, { notes });
      return data;
    },
    onSuccess: (_, { loanId }) => {
      qc.invalidateQueries({ queryKey: ['approval-workflows', loanId] });
      qc.invalidateQueries({ queryKey: ['pending-approvals'] });
      invalidateLoanActivity(qc, loanId);
    },
  });
}

// Custom action endpoint /step — keep manual
export function useSubmitApprovalStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      loanId,
      workflowId,
      action,
      comments,
      conditions,
    }: {
      loanId: string;
      workflowId: string;
      action: string;
      comments?: string;
      conditions?: string;
    }) => {
      const { data } = await api.post(`/loans/${loanId}/approvals/workflow/${workflowId}/step`, {
        action,
        comments,
        conditions,
      });
      return data;
    },
    onSuccess: (_, { loanId }) => {
      qc.invalidateQueries({ queryKey: ['approval-workflows', loanId] });
      qc.invalidateQueries({ queryKey: ['pending-approvals'] });
      invalidateLoanActivity(qc, loanId);
    },
  });
}
